import { Box, Button, Divider, Group, Progress, Stack, Text, Tooltip } from '@mantine/core';
import { IconArrowRight, IconExternalLink } from '@tabler/icons-react';
import type { ProductCatalogueEntry } from '../catalogue/products';
import type { ProductEntitlement } from '../types/entitlements';
import type { CardSetupState } from '../lib/provisioning';
import { BadgeBlock } from './BadgeBlock';

type Props = {
  product: ProductCatalogueEntry;
  entitlement: ProductEntitlement | undefined;
  ctaLabel?: string;
  isLocked?: boolean;
  onCta?: () => void;
  ctaBusy?: boolean;
  // Holds the button without a spinner, e.g. while another start for the org is in flight.
  ctaDisabled?: boolean;
  badge?: {
    title: string;
    color: string;
  };
  // While the org's instance is provisioned the card must not offer an Open that leads
  // nowhere: 'building' clears the card down to one calm message, 'stalled' keeps the
  // layout but says the build is taking longer — neither renders as a link.
  setupState?: CardSetupState;
  // Overrides where Open points (see productHref): string = use it, null = no valid
  // destination so render no link, absent = the catalogue's relative path.
  href?: string | null;
  // A line of state ("Waiting for an admin · requested 2 hours ago") shown where the call to
  // action would be; the card stays live-looking but offers neither a button nor a link.
  note?: string;
};

function BuildingCard({ product }: Readonly<{ product: ProductCatalogueEntry }>) {
  return (
    <Box data-product-tile={product.key} style={{ '--product-color': product.brandColour }}>
      <Stack p={24} gap={8} align="center" justify="center" h="100%" mih={170} style={{ textAlign: 'center' }}>
        <Stack w={46}>
          <img src={product.logo} alt={product.name} style={{ width: '100%', height: 'auto' }} />
        </Stack>
        <Text size="md" fw={800} lh={'normal'} c="var(--text-default)" style={{ letterSpacing: -0.2 }}>
          {product.name}
        </Text>
        <Text size="xs" c="var(--text-muted)">Setting up your space — usually a few minutes.</Text>
        <Progress
          value={100}
          animated
          color={product.brandColour}
          size="xs"
          w={96}
          aria-label={`Setting up ${product.name}`}
        />
      </Stack>
    </Box>
  );
}

type CtaProps = {
  entitlement: ProductEntitlement | undefined;
  ctaLabel?: string;
  onCta?: () => void;
  ctaBusy?: boolean;
  ctaDisabled?: boolean;
  href?: string;
  isStalled: boolean;
  isLinked: boolean;
  isActive: boolean;
  note?: string;
};

function CardCta({ entitlement, ctaLabel, onCta, ctaBusy, ctaDisabled, href, isStalled, isLinked, isActive, note }: Readonly<CtaProps>) {
  if (isStalled) {
    return (
      <Group fz={12} fw={700} c={'var(--status-warning)'}>
        Still setting up…
      </Group>
    );
  }
  if (note) {
    return <Text fz={12} fw={600} c="var(--text-muted)">{note}</Text>;
  }
  if (isLinked && isActive) {
    return (
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group fz={12} fw={700} c={'var(--accent-text)'} gap={5}>
          Open
          <IconArrowRight size={14} />
        </Group>
        <Tooltip label="Open in new tab" position="top" fz={'xs'} fw={500}>
          <Button variant='subtle' h={'auto'} p={0} bg={'transparent'} flex={'none'}
            style={{
              '--button-hover-color': 'var(--accent-text)',
              '--button-color': 'var(--text-subtle)'
            }}
            onClick={e => {
              e.preventDefault();
              window.open(href, '_blank', 'noopener,noreferrer');
            }}
          >
            <IconExternalLink size={16} />
          </Button>
        </Tooltip>
      </Group>
    );
  }
  if (onCta && !isActive) {
    return (
      <Button
        variant="light"
        size="xs"
        loading={ctaBusy}
        disabled={ctaDisabled}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCta(); }}
        styles={{ root: { alignSelf: 'flex-start', '--button-color': 'var(--accent-text)' } }}
      >
        {ctaLabel ?? 'Get started'}
      </Button>
    );
  }
  return (
    <Group fz={12} fw={700} c={'var(--accent-text)'}>
      {ctaLabel ?? entitlement?.cta?.replaceAll('_', ' ') ?? 'Coming soon'}
    </Group>
  );
}

export function ProductBlock({ product, entitlement, ctaLabel, isLocked, onCta, ctaBusy, ctaDisabled, badge, setupState, href: hrefProp, note }: Readonly<Props>) {
  const status = entitlement?.status ?? 'available';
  const href = hrefProp === undefined ? product.path : (hrefProp ?? undefined);

  if (setupState === 'building') return <BuildingCard product={product} />;

  const isStalled = setupState === 'stalled';
  const isActive = status === 'active' && !isLocked;
  const isLinked = !isLocked && Boolean(href) && !isStalled;
  // Grey a card out only when there's nothing to do on it. An active, linked
  // product opens; a product offering a trial/CTA is actionable too — don't
  // disable-style those just because the subscription isn't active yet. A stalled
  // build is active work, not a dead card, so it keeps full presence as well.
  const isActionable = (isActive && isLinked) || Boolean(onCta) || isStalled || Boolean(note);

  return (
    <Box
      data-product-tile={product.key}
      style={{ '--product-color': product.brandColour, cursor: isActionable ? undefined : 'not-allowed' }}
      data-disabled={!isActionable || undefined}
      component={isLinked ? 'a' : 'div'}
      href={isLinked ? href : undefined}
      target={isLinked ? '_self' : undefined}

    >
      <Stack p={24} gap={'sm'} style={{ position: 'relative' }} opacity={isActionable ? undefined : 0.5} >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack w={46}>
            <img src={product.logo} alt={product.name} style={{ width: '100%', height: 'auto' }} />
          </Stack>
          {badge && <BadgeBlock children={badge.title} bdrs={'md'} color={badge.color} />}

        </Group>

        <Stack gap={3}>
          <Text size="md" fw={800} lh={'normal'} c="var(--text-default)" style={{ letterSpacing: -0.2 }}>
            {product.name}
          </Text>
          <Text size="xs" c="var(--text-muted)" style={{ lineHeight: 1.55 }}>
            {product.tagline}
          </Text>
        </Stack>
        <Divider color='var(--border-subtle)'></Divider>

        <CardCta
          entitlement={entitlement}
          ctaLabel={ctaLabel}
          onCta={onCta}
          ctaBusy={ctaBusy}
          ctaDisabled={ctaDisabled}
          href={href}
          isStalled={isStalled}
          isLinked={isLinked}
          isActive={isActive}
          note={note}
        />
      </Stack>
    </Box>
  );
}
